import {
  FIRST_POSITION,
  positionAfter
} from "@pathwright/utils/src/positioning/fractional-positioning"
import PanelSection, {
  AugmentationPanelItemProps,
  AugmentationPanelSectionAddLinkProps,
  AugmentationPanelSectionAddProps,
  AugmentationPanelSectionAddSelectProps,
  AugmentationPanelSectionListSortProps
} from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { ItemSelectorOption } from "@pathwright/ux/src/components/item-selector/ItemSelector"
import { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { DocumentNode } from "graphql"
import { useMemo } from "react"
import { PositioningContextInput } from "@pathwright/graphql-client/src/generated"
import {
  PositionedObject,
  PositioningContextProvider,
  usePositioningContext
} from "../../positioning/PositioningContext"
import { getCoverPagePanelSectionElementId } from "../coverPageHashNavigation"
import {
  CoverPageHashSectionKey,
  CoverPagePanelSectionKey,
  toCoverPageScrollHashKey
} from "../types"

export type CoverPagePanelAddProps<
  T extends ItemSelectorOption = ItemSelectorOption
> = Pick<AugmentationPanelSectionAddProps<T>, "itemKey" | "label" | "hashKey"> &
  (
    | { select: AugmentationPanelSectionAddSelectProps<T> }
    | { link: AugmentationPanelSectionAddLinkProps }
  )

type CoverPagePanelSectionSharedProps = {
  heading: string
  items: AugmentationPanelItemProps[]
  headingLinkProps?: LinkProps
}

type CoverPagePanelSectionScrollProps = {
  scrollHashKey?: string
  panelSectionElementId?: string
}

type CoverPagePanelSectionLayoutProps = CoverPagePanelSectionSharedProps &
  CoverPagePanelSectionScrollProps & {
    addProps?: AugmentationPanelSectionAddProps<any>
    sortProps?: AugmentationPanelSectionListSortProps
  }

type CoverPageSortableSectionBlockProps = CoverPagePanelSectionSharedProps &
  CoverPagePanelSectionScrollProps & {
    addProps?: AugmentationPanelSectionAddProps<any>
    positionedItems: PositionedObject[]
  }

type CoverPagePanelSortConfig = {
  positionedItems: PositionedObject[]
  itemFragmentDocument: DocumentNode
  itemKey: string
  positioningContext: PositioningContextInput
  fragmentVariables: Record<string, PositioningContextInput>
}

export type CoverPagePanelSectionBlockProps =
  CoverPagePanelSectionSharedProps & {
    addProps?: AugmentationPanelSectionAddProps<any>
    sortProps?: AugmentationPanelSectionListSortProps | CoverPagePanelSortConfig
    /** Panel or page section id; normalized to a scroll hash before passing to the panel UI. */
    sectionKey?: CoverPagePanelSectionKey | CoverPageHashSectionKey
  }

function withSequentialPositions<T extends PositionedObject>(items: T[]): T[] {
  let previousPosition = ""

  return items.map((item, index) => {
    const position =
      index === 0 ? FIRST_POSITION : positionAfter(previousPosition)
    previousPosition = position

    return {
      ...item,
      position
    }
  })
}

function getCoverPagePanelSectionScroll(
  sectionKey?: CoverPagePanelSectionKey | CoverPageHashSectionKey
) {
  const scrollHashKey = sectionKey
    ? toCoverPageScrollHashKey(sectionKey)
    : undefined

  return {
    scrollHashKey,
    panelSectionElementId: scrollHashKey
      ? getCoverPagePanelSectionElementId(scrollHashKey)
      : undefined
  }
}

function useCoverPagePanelListSortProps<T extends PositionedObject>(
  items: AugmentationPanelItemProps[],
  positionedItems: T[]
) {
  const { updatePositionedObject } = usePositioningContext()

  const positionedItemByKey = useMemo(
    () =>
      new Map(
        items
          .filter((item) => item.sortable !== false)
          .map((item, index) => [item.itemKey, positionedItems[index]])
      ),
    [items, positionedItems]
  )

  return useMemo<AugmentationPanelSectionListSortProps>(
    () => ({
      onSort: async ({ item, index, sortableItems }) => {
        const movedItem = positionedItemByKey.get(item.itemKey)
        if (!movedItem) {
          throw new Error(`Unable to sort missing item ${item.itemKey}.`)
        }

        await updatePositionedObject({
          item: movedItem,
          index
        })
      },
      onMove: async (item, index) => {
        const movedItem = positionedItemByKey.get(item.itemKey)
        if (!movedItem) {
          throw new Error(`Unable to sort missing item ${item.itemKey}.`)
        }

        await updatePositionedObject({
          item: movedItem,
          index
        })
      }
    }),
    [positionedItemByKey, updatePositionedObject]
  )
}

function useCoverPagePanelAddProps<T extends ItemSelectorOption>(
  addProps?: AugmentationPanelSectionAddProps<T>
) {
  return useMemo<AugmentationPanelSectionAddProps<T> | undefined>(() => {
    if (!addProps) return undefined

    if (!("select" in addProps)) {
      return addProps
    }

    return {
      ...addProps,
      select: {
        ...addProps.select,
        mode: "create-or-select"
      }
    }
  }, [addProps])
}

function CoverPagePanelSectionLayout({
  heading,
  items,
  addProps,
  sortProps,
  scrollHashKey,
  panelSectionElementId,
  headingLinkProps
}: CoverPagePanelSectionLayoutProps) {
  const showList = items.length > 0 || addProps

  return (
    <PanelSection.Root id={panelSectionElementId}>
      <PanelSection.Heading
        linkProps={headingLinkProps}
        hashKey={scrollHashKey}
      >
        {heading}
      </PanelSection.Heading>
      {showList ? (
        <PanelSection.Content>
          <PanelSection.List
            items={items}
            addProps={addProps}
            sortProps={sortProps}
            hashKey={scrollHashKey}
          />
        </PanelSection.Content>
      ) : null}
    </PanelSection.Root>
  )
}

// Must render inside PositioningContextProvider for usePositioningContext.
function CoverPageSortableSectionBlock({
  positionedItems,
  ...layoutProps
}: CoverPageSortableSectionBlockProps) {
  const listSortProps = useCoverPagePanelListSortProps(
    layoutProps.items,
    positionedItems
  )

  return (
    <CoverPagePanelSectionLayout {...layoutProps} sortProps={listSortProps} />
  )
}

export function CoverPagePanelSectionBlock({
  heading,
  items,
  addProps,
  sortProps,
  sectionKey,
  headingLinkProps
}: CoverPagePanelSectionBlockProps) {
  const resolvedAddProps = useCoverPagePanelAddProps(addProps)
  const { scrollHashKey, panelSectionElementId } =
    getCoverPagePanelSectionScroll(sectionKey)

  const layoutProps = {
    heading,
    items,
    addProps: resolvedAddProps,
    scrollHashKey,
    panelSectionElementId,
    headingLinkProps
  }

  if (sortProps) {
    // Custom sort (bypass the normal PositioningContext)
    if ("onSort" in sortProps) {
      return (
        <CoverPagePanelSectionLayout {...layoutProps} sortProps={sortProps} />
      )
    }

    const {
      positionedItems,
      itemFragmentDocument,
      itemKey,
      positioningContext,
      fragmentVariables
    } = sortProps

    return (
      <PositioningContextProvider
        items={positionedItems}
        itemKey={itemKey}
        itemFragmentDocument={itemFragmentDocument}
        context={positioningContext}
        fragmentVariables={fragmentVariables}
      >
        <CoverPageSortableSectionBlock
          {...layoutProps}
          positionedItems={positionedItems}
        />
      </PositioningContextProvider>
    )
  }

  return <CoverPagePanelSectionLayout {...layoutProps} />
}
